import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { BeWizrLibraryCalendarItemsEndpoint, BeWizrLibraryItemsEndpoint } from '~/stores'
import { observer } from '~/ui/component'
import { Center, EmptyOrFetching, HBox, List, ListProps, Scroller, Spinner, VBox } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import { screenWidths } from '~/ui/styling/layout'
import BeWizrLibraryFilters from './BeWizrLibraryFilters'
import renderLibraryItem from './items'
import { Props as BeWizrLibraryFiltersProps } from './BeWizrLibraryFilters'
import { BeWizrLibraryItem } from '~/models'

export interface Props {
  endpoint:    BeWizrLibraryItemsEndpoint | BeWizrLibraryCalendarItemsEndpoint
  filters?:    Partial<Pick<BeWizrLibraryFiltersProps, 'type' | 'category' | 'topic'>>
  renderList?: (listProps: BeWizrLibraryViewListRenderProps) => React.ReactNode
}

export interface BeWizrLibraryViewListRenderProps extends ListProps<BeWizrLibraryItem> {
  data: BeWizrLibraryItem[]
}

const BeWizrLibraryView = observer('BeWizrLibraryView', (props: Props) => {

  const {endpoint, filters = {}, renderList} = props

  const [t] = useTranslation('bewizr-library')

  const fetchStatus = endpoint.fetchStatus
  const items       = endpoint.data

  const fetchMore = React.useCallback(() => {
    return endpoint?.fetchMore()
  }, [endpoint])

  const filtered = React.useMemo(
    () => Object.keys(endpoint.allParams).length > 0,
    [endpoint.allParams],
  )

  const isTablet = useMediaQuery({minWidth: screenWidths.tablet})

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return isTablet ? (
      <HBox align='stretch' flex>
        {renderContent()}
      </HBox>
    ) : (
      <VBox flex>
        {renderContent()}
      </VBox>
    )
  }

  function renderContent() {
    const listProps: BeWizrLibraryViewListRenderProps = {
      data:            items,
      onEndReached:    fetchMore,
      renderItem:      renderLibraryItem,
      EmptyComponent:  renderEmpty,
      FooterComponent: renderFooter,
      itemGap:         layout.padding.s,
      scrollable:      true,
      keyExtractor:    item => item.id,
    }
     return (
      <>
        {renderFilters()}
        <VBox flex>
          {renderList == null ? (
          <List
            {...listProps}
            contentPadding={layout.padding.m}
          />
        ) : renderList(listProps)}
        </VBox>
      </>
     )
  }

  const renderEmpty = React.useCallback(() => (
    <EmptyOrFetching
      {...t(`empty.${filtered ? 'filtered' : 'not_filtered'}`)}
      status={fetchStatus}
      flex
    />
  ), [t, filtered, fetchStatus])

  const renderFooter = React.useCallback(() => {
    if (fetchStatus !== 'fetching' || items.length === 0) { return null }

    return (
      <Center classNames={$.footer}>
        <Spinner/>
      </Center>
    )
  }, [fetchStatus, items.length, $.footer])

  function renderFilters() {
    return (
      <Scroller flex='shrink' classNames={$.filters} contentPadding={layout.padding.m}>
        <VBox gap={layout.padding.s}>
          <BeWizrLibraryFilters
            endpoint={endpoint}
            {...filters}
          />
        </VBox>
      </Scroller>
    )
  }

  return render()

})

export default BeWizrLibraryView

const useStyles = createUseStyles(theme => ({
  filters: {
    ...layout.tablet({
      width: 264,
      borderRight: [2, 'solid', theme.colors.border.dimmer],
    }),
  },

  footer: {
    ...layout.responsive(size => ({
      padding: layout.padding.m[size],
    })),
  },
}))